import { createContext, useContext, ReactNode, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export type UserActivityTabType = "details" | "activity" | "history" | null;

type UserActivityContextType = {
  isOpen: boolean;
  userId: string | null;
  tab: UserActivityTabType;
  openUserActivity: (userId: string, tab?: UserActivityTabType) => void;
  closeUserActivity: () => void;
};

const UserActivityContext = createContext<UserActivityContextType | undefined>(
  undefined
);

export function UserActivityProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get values from URL query parameters
  const userId = searchParams.get("userId");
  const tab = searchParams.get("userTab") as UserActivityTabType;
  const isOpen = userId !== null;

  const openUserActivity = useCallback(
    (id: string, tab?: UserActivityTabType) => {
      searchParams.set("userId", id);
      if (tab) searchParams.set("userTab", tab);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const closeUserActivity = useCallback(() => {
    // Remove userId parameter from URL
    searchParams.delete("userId");
    searchParams.delete("userTab");
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  return (
    <UserActivityContext.Provider
      value={{
        isOpen,
        userId,
        tab,
        openUserActivity,
        closeUserActivity,
      }}
    >
      {children}
    </UserActivityContext.Provider>
  );
}

export function useUserActivity() {
  const context = useContext(UserActivityContext);
  if (context === undefined) {
    throw new Error(
      "useUserActivity must be used within a UserActivityProvider"
    );
  }
  return context;
}
